import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {PullRequestStateIcons} from '@github-ui/issue-pr-state/Icons'
import {ListItem} from '@github-ui/list-view/ListItem'
import {ListItemDescription} from '@github-ui/list-view/ListItemDescription'
import {ListItemLeadingContent} from '@github-ui/list-view/ListItemLeadingContent'
import {ListItemLeadingVisual} from '@github-ui/list-view/ListItemLeadingVisual'
import {ListItemMainContent} from '@github-ui/list-view/ListItemMainContent'
import {ListItemMetadata} from '@github-ui/list-view/ListItemMetadata'
import {ListItemTitle} from '@github-ui/list-view/ListItemTitle'
import {useListViewVariant} from '@github-ui/list-view/ListViewVariantContext'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {useAnalytics} from '@github-ui/use-analytics'
import {CommentIcon} from '@primer/octicons-react'
import {SkeletonBox} from '@primer/react/experimental'
import {clsx} from 'clsx'
import type {ComponentProps, ReactNode} from 'react'
import styles from './PullsListItem.module.css'
import {AssigneeAvatars} from '../shared/AssigneeAvatars'
import compactStyles from '../shared/CompactListItem.module.css'
import {renderLabelBadges} from '../shared/LabelBadges'
import {unreadRowClassName, UnreadRowLabel} from '../shared/UnreadRowIndicator'
import inlineCodeStyles from '../shared/TitleInlineCode.module.css'
import type {EnrichedPullListItem} from './types/list-types'
import {PullsListItemTimestampContainer} from './PullsListItemTimestampContainer'
import {
  reviewDecision,
  reviewDecisionCompact,
  statusCheckDisplay,
  statusCheckDisplayCompact,
} from './helpers/list-helpers'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {PullsChecksStatusBadge} from './PullsChecksStatusBadge'
import {StackBadge} from '@github-ui/stack-badge'
import {deriveAttributedUsername, isAgentAuthor} from './helpers/derive-attributed-username'
import {NonInteractiveTooltip} from './helpers/NonInteractiveTooltip'
import {PR_STATE_FALLBACK_TOOLTIP_LABEL, PR_STATE_TOOLTIP_LABELS} from './consts/pr-state-consts'
import {getReactionSortDisplay} from '../shared/consts/sorting-consts'

export type PullsListItemLinkProps = Omit<
  NonNullable<ComponentProps<typeof ListItemTitle>['linkProps']>,
  'href' | 'onClick'
>

interface PullsListItemProps {
  pull: EnrichedPullListItem
  onFilterSelect?: (type: 'repo' | 'author' | 'label' | 'assignee', value: string) => void
  isDeferredLoading?: boolean
  isAgentAttributionEnabled?: boolean
  sortingItemSelected?: string
  showRepository?: boolean
  titleLinkProps?: PullsListItemLinkProps
}

function renderCompactMetadataValue(value: ReactNode) {
  return value === null || value === undefined || value === '' ? (
    <span className={compactStyles.emptyPlaceholder}>&mdash;</span>
  ) : (
    value
  )
}

export function PullsListItem({
  pull,
  onFilterSelect,
  isDeferredLoading,
  isAgentAttributionEnabled,
  sortingItemSelected,
  showRepository = true,
  titleLinkProps,
}: PullsListItemProps) {
  const {sendAnalyticsEvent} = useAnalytics()
  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const {variant} = useListViewVariant()
  const isCompact = variant === 'compact'
  const {isBrowser} = useRuntimeEnvironment()

  const commitChecksDialogEnabled = isBrowser && isFeatureEnabled('pull_request_commit_checks_dialog')
  const attributedUsername = isAgentAttributionEnabled ? deriveAttributedUsername(pull) : undefined
  const isPullAgentAuthored = isAgentAttributionEnabled ? isAgentAuthor(pull) : false

  const reactionSortDisplay = getReactionSortDisplay(sortingItemSelected)
  const reactionCount = reactionSortDisplay
    ? (pull.reactionGroups?.find(group => group.content === reactionSortDisplay.content)?.totalCount ?? 0)
    : 0

  // `displayState` is optional in the payload (the backend derives it from a
  // nilable `PullRequest::Icon::State`), so guard the icon lookup: an unknown or
  // missing state would otherwise resolve to `undefined` and crash the row when
  // rendered. Fall back to the neutral open pull-request glyph, pairing with the
  // existing `PR_STATE_FALLBACK_TOOLTIP_LABEL`.
  const PRStateIcon =
    PullRequestStateIcons[pull.displayState as keyof typeof PullRequestStateIcons] ?? PullRequestStateIcons.OPEN
  const prStateTooltipLabel =
    PR_STATE_TOOLTIP_LABELS[pull.displayState as keyof typeof PullRequestStateIcons] ?? PR_STATE_FALLBACK_TOOLTIP_LABEL
  const [owner, repo] = pull.repoNameWithOwner.split('/')
  const hovercardAttributes = getHovercardAttributesForType('pull_request', {
    owner,
    repo,
    pullRequestNumber: pull.number,
  })
  const onTitleClick = () =>
    sendAnalyticsEvent('pull-requests.click', 'LIST_ITEM_TITLE', {displayState: pull.displayState})
  const labelBadges = !isCompact
    ? renderLabelBadges({
        labels: pull.labels,
        onSelect: onFilterSelect ? label => onFilterSelect('label', label) : undefined,
        filterLinkClassName: styles.filterLink,
      })
    : undefined

  const compactRepoName =
    onFilterSelect && showRepository ? (
      <button
        type="button"
        className={styles.filterLink}
        onClick={() => onFilterSelect('repo', pull.repoNameWithOwner)}
        data-testid="repo-filter-link"
        aria-label={`Filter by repository ${pull.repoNameWithOwner}`}
      >
        {pull.repoNameWithOwner}
      </button>
    ) : showRepository ? (
      <span>{pull.repoNameWithOwner}</span>
    ) : (
      <span>#{pull.number}</span>
    )

  const renderAuthorLink = (login: string, testId: string) =>
    onFilterSelect ? (
      <button
        type="button"
        className={styles.filterLink}
        onClick={() => onFilterSelect('author', login)}
        data-testid={testId}
        aria-label={`Filter by author ${login}`}
      >
        {login}
      </button>
    ) : (
      <span>{login}</span>
    )

  const compactAuthor = attributedUsername ? (
    <>
      {renderAuthorLink(attributedUsername, 'attributed-author-filter-link')}
      <span> with </span>
      {renderAuthorLink(pull.author.displayLogin, 'author-filter-link')}
    </>
  ) : (
    renderAuthorLink(pull.author.displayLogin, 'author-filter-link')
  )

  const compactStatus = pull.statusCheckRollup ? (
    commitChecksDialogEnabled && pull.headSha ? (
      <PullsChecksStatusBadge
        headSha={pull.headSha}
        repositoryNwo={pull.repoNameWithOwner}
        statusCheckRollup={pull.statusCheckRollup}
        aliveChannel={pull.commitHeadShaChannel}
      />
    ) : (
      statusCheckDisplayCompact(pull.statusCheckRollup)
    )
  ) : null

  const metadata = isCompact ? (
    <>
      <ListItemMetadata className={clsx(compactStyles.metadataCell, compactStyles.metadataReview)}>
        {renderCompactMetadataValue(
          pull.reviewDecisionState
            ? reviewDecisionCompact(pull.reviewDecisionState, {
                approvedReviewsCount: pull.approvedReviewsCount,
                changesRequestedReviewsCount: pull.changesRequestedReviewsCount,
              })
            : null,
        )}
      </ListItemMetadata>
      <ListItemMetadata className={clsx(compactStyles.metadataCell, compactStyles.metadataStatus)}>
        {renderCompactMetadataValue(compactStatus)}
      </ListItemMetadata>
      <ListItemMetadata className={clsx(compactStyles.metadataCell, compactStyles.metadataUtility)}>
        {renderCompactMetadataValue(
          pull.commentCount > 0 ? (
            <>
              <CommentIcon aria-label={`${pull.commentCount} comments`} size={16} /> {pull.commentCount}
            </>
          ) : null,
        )}
      </ListItemMetadata>
      {reactionSortDisplay ? (
        <ListItemMetadata className={clsx(compactStyles.metadataCell, compactStyles.metadataUtility)}>
          {renderCompactMetadataValue(
            reactionCount > 0 ? (
              <>
                <span
                  role="img"
                  aria-label={`${reactionCount} ${reactionSortDisplay.label} reaction${reactionCount === 1 ? '' : 's'}`}
                >
                  {reactionSortDisplay.emoji}
                </span>{' '}
                <span aria-hidden="true">{reactionCount}</span>
              </>
            ) : null,
          )}
        </ListItemMetadata>
      ) : null}
      <ListItemMetadata className={clsx(compactStyles.metadataCell, compactStyles.metadataAssignees)}>
        {renderCompactMetadataValue(
          pull.assignees && pull.assignees.length > 0 ? (
            <AssigneeAvatars
              assignees={pull.assignees}
              onSelect={onFilterSelect ? login => onFilterSelect('assignee', login) : undefined}
              filterLinkClassName={styles.assigneeFilterButton}
            />
          ) : null,
        )}
      </ListItemMetadata>
    </>
  ) : (
    <>
      {pull.commentCount > 0 ? (
        <ListItemMetadata>
          <CommentIcon size={16} /> {pull.commentCount}
        </ListItemMetadata>
      ) : null}
      {reactionSortDisplay && reactionCount > 0 ? (
        <ListItemMetadata>
          <span
            role="img"
            aria-label={`${reactionCount} ${reactionSortDisplay.label} reaction${reactionCount === 1 ? '' : 's'}`}
          >
            {reactionSortDisplay.emoji}
          </span>{' '}
          <span aria-hidden="true">{reactionCount}</span>
        </ListItemMetadata>
      ) : null}
      {pull.assignees && pull.assignees.length > 0 && (
        <ListItemMetadata alignment="right">
          <AssigneeAvatars
            assignees={pull.assignees}
            onSelect={onFilterSelect ? login => onFilterSelect('assignee', login) : undefined}
            filterLinkClassName={styles.assigneeFilterButton}
            alignRight
          />
        </ListItemMetadata>
      )}
    </>
  )

  return (
    <ListItem
      key={pull.id}
      className={unreadRowClassName(pull.isReadByCurrentUser, isCompact ? styles.listItemCompact : styles.listItem)}
      title={
        <ListItemTitle
          href={pull.permalink}
          onClick={onTitleClick}
          value={pull.titleHtml ? <VerifiedHTMLText as="span" html={pull.titleHtml} /> : <span>{pull.title}</span>}
          linkProps={{
            ...titleLinkProps,
            ...hovercardAttributes,
            href: pull.permalink,
            onClick: onTitleClick,
            'data-testid': 'listitem-title-link',
          }}
          anchorClassName={clsx(styles.titleLink, inlineCodeStyles.inlineCodeTitle)}
          trailingBadges={labelBadges}
        />
      }
      metadata={metadata}
      metadataContainerClassName={isCompact ? compactStyles.metadataContainer : undefined}
    >
      <ListItemLeadingContent>
        <UnreadRowLabel isRead={pull.isReadByCurrentUser} />
        <ListItemLeadingVisual>
          <NonInteractiveTooltip label={prStateTooltipLabel} direction="e" includeAriaAttributes={false}>
            <PRStateIcon aria-label={prStateTooltipLabel} />
          </NonInteractiveTooltip>
        </ListItemLeadingVisual>
      </ListItemLeadingContent>
      <ListItemMainContent>
        {isCompact ? (
          <ListItemDescription className={compactStyles.leftMetadata}>
            <span className={compactStyles.leftMetadataText}>{compactRepoName}</span>
            <span className={compactStyles.leftMetadataSeparator} aria-hidden="true">
              •
            </span>
            <span className={compactStyles.leftMetadataText}>{compactAuthor}</span>
            {pull.isDraft ? (
              <>
                <span className={compactStyles.leftMetadataSeparator} aria-hidden="true">
                  •
                </span>
                <span className={compactStyles.leftMetadataText}>Draft</span>
              </>
            ) : null}
          </ListItemDescription>
        ) : (
          <ListItemDescription className={styles.description}>
            <span>
              {showRepository ? compactRepoName : null}
              <span>#{pull.number} </span>
            </span>
            <PullsListItemTimestampContainer
              createdAt={new Date(pull.createdAt)}
              updatedAt={new Date(pull.updatedAt)}
              prState={pull.state}
              author={pull.author.displayLogin}
              isAgent={isPullAgentAuthored}
              attributedUsername={attributedUsername}
              onAuthorClick={onFilterSelect ? author => onFilterSelect('author', author) : undefined}
            />
            {pull.isDraft ? <span> &middot; Draft</span> : null}
            <StackBadge
              pullRequestNumber={pull.number}
              stackPosition={pull.stackPosition}
              stackSize={pull.stackSize}
              repoNameWithOwner={pull.repoNameWithOwner}
            />
            {pull.reviewDecisionState ? (
              reviewDecision(pull.reviewDecisionState, {
                approvedReviewsCount: pull.approvedReviewsCount,
                changesRequestedReviewsCount: pull.changesRequestedReviewsCount,
              })
            ) : isDeferredLoading ? (
              <SkeletonBox className={styles.inlineSkeleton} width="100px" />
            ) : null}
            {pull.statusCheckRollup ? (
              commitChecksDialogEnabled && pull.headSha ? (
                <span className={styles.inlineChecksBadge}>
                  <PullsChecksStatusBadge
                    headSha={pull.headSha}
                    repositoryNwo={pull.repoNameWithOwner}
                    statusCheckRollup={pull.statusCheckRollup}
                    aliveChannel={pull.commitHeadShaChannel}
                  />
                </span>
              ) : (
                statusCheckDisplay(pull.statusCheckRollup)
              )
            ) : isDeferredLoading ? (
              <SkeletonBox className={styles.inlineSkeleton} height="1lh" width="48px" />
            ) : null}
          </ListItemDescription>
        )}
      </ListItemMainContent>
    </ListItem>
  )
}
